// v2.71.87 — Centralized <GoogleMapsUrlInput>. Lossless URL storage +
// best-effort coord extraction.
//
// Behavioural rules (per v2.71.87 user spec)
// ──────────────────────────────────────────
//   1. Accept ALL valid Google Maps URL shapes — including short
//      (maps.app.goo.gl, goo.gl/maps).
//   2. **Store the exact URL the user typed.** Never modify it on save.
//   3. Coords are extracted in the background as a BONUS for map preview.
//      If extraction fails (e.g. short URL hits a place-share landing
//      that has no lat/lng), silently fall back — don't show an error.
//   4. Show an error ONLY when the URL host is not a Google domain at all.
//   5. The "View on Map" button on detail pages already uses the saved
//      URL via `<a href={p.google_maps_link}>` — no change there.
//
// Diff vs v2.71.73
// ────────────────
//   - We NO LONGER replace `value` with the resolved canonical URL. The
//     user's pasted text is the source of truth — coords are derived
//     out-of-band.
//   - We NO LONGER show an error / amber pill for short URLs whose
//     resolution didn't yield coords. The URL is still valid (the lister
//     pasted exactly what Google gave them); only the map-preview hint
//     is unavailable, which we surface as a soft "Saved" tick instead
//     of an error.

import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { Input } from "../../components/ui/input";
import api from "../services/api";
import {
  parseGoogleMapsUrl,
  isShortMapsUrl,
  resolveMapsUrl,
} from "../utils/mapsUrl";

/**
 * @param {object} props
 * @param {string} props.value                  - current URL string
 * @param {(url:string, coords:{lat,lng}|null) => void} props.onChange
 *        Called whenever the URL OR the resolved coords change. v2.71.87:
 *        the URL passed back is ALWAYS the verbatim text the user typed,
 *        never the resolved canonical URL.
 * @param {string} [props.testIdPrefix="gmaps"] - data-testid prefix
 * @param {string} [props.placeholder]
 * @param {string} [props.inputClassName]
 * @param {string} [props.emptyHint]            - helper line shown when empty
 * @param {boolean} [props.autoResolve=true]    - call /api/maps/resolve in
 *        the background for short / indeterminate URLs to surface a
 *        map-preview coord hint. Set false to skip net entirely.
 */
export default function GoogleMapsUrlInput({
  value,
  onChange,
  testIdPrefix = "gmaps",
  placeholder = "Paste any Google Maps link (mobile share, maps.app.goo.gl, full URL…)",
  inputClassName = "rounded-lg h-11",
  emptyHint = "",
  autoResolve = true,
}) {
  // status: idle | resolving | coords | saved
  //   • idle      → empty input
  //   • resolving → background resolver running
  //   • coords    → coords extracted (locally or via backend) — show tick + lat/lng
  //   • saved     → URL is valid but no coords available — show tick only
  const [status, setStatus] = useState("idle");
  const [resolvedCoords, setResolvedCoords] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const raw = (value || "").trim();
  const local = parseGoogleMapsUrl(raw);
  const showError = !local.valid && raw.length > 0;
  const hasLocalCoords = local.valid && !!local.coords;

  // Re-resolve whenever URL changes. Debounce 350ms. Best-effort only —
  // failures are silent (the URL itself is fully accepted regardless).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!raw) {
      setStatus("idle");
      setResolvedCoords(null);
      return;
    }
    if (!local.valid) {
      // Host isn't Google → handled by the error pill; nothing else to do.
      setStatus("idle");
      setResolvedCoords(null);
      return;
    }
    if (hasLocalCoords) {
      setStatus("coords");
      setResolvedCoords(local.coords);
      onChange?.(raw, local.coords);
      return;
    }
    if (!autoResolve) {
      // Skip net but still accept the URL.
      setStatus("saved");
      setResolvedCoords(null);
      onChange?.(raw, null);
      return;
    }

    // Background resolver (silent on failure).
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      setStatus("resolving");
      const result = await resolveMapsUrl(raw, api, { signal: controller.signal });
      if (controller.signal.aborted || result.cancelled) return;
      if (result.ok && result.coords) {
        setStatus("coords");
        setResolvedCoords(result.coords);
        // v2.71.87 — pass back the USER'S URL, not the resolved one.
        onChange?.(raw, result.coords);
      } else {
        // v2.71.87 — short URL or indeterminate full URL that didn't yield
        // coords is STILL valid. The link is accepted; we just can't preview
        // a pin. No error UI — just the soft "saved" tick.
        setStatus("saved");
        setResolvedCoords(null);
        onChange?.(raw, null);
      }
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, autoResolve]);

  const handleInput = (e) => {
    const next = e.target.value;
    if (!next) {
      onChange?.("", null);
      setStatus("idle");
      setResolvedCoords(null);
      return;
    }
    // Push the raw text up immediately so parent always has the latest typed
    // value. Background resolver effect will follow up with coords (if any).
    onChange?.(next, null);
  };

  return (
    <>
      <Input
        type="url"
        value={value || ""}
        onChange={handleInput}
        placeholder={placeholder}
        className={`${inputClassName} ${showError ? "border-[#DC2626] focus-visible:ring-[#DC2626]" : ""}`}
        data-testid={`${testIdPrefix}-google-maps-url`}
        inputMode="url"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
      />

      {/* Host error — ONLY for genuinely non-Google URLs (per v2.71.87 spec) */}
      {showError && (
        <div
          className="mt-1 text-[11px] text-[#DC2626] inline-flex items-start gap-1"
          data-testid={`${testIdPrefix}-google-maps-url-error`}
        >
          <AlertCircle size={11} className="mt-[1px] shrink-0" />
          <span>{local.reason}</span>
        </div>
      )}

      {/* Background resolver running */}
      {status === "resolving" && (
        <div
          className="mt-1 text-[11px] text-zinc-600 inline-flex items-center gap-1.5"
          data-testid={`${testIdPrefix}-google-maps-url-resolving`}
        >
          <Loader2 size={11} className="animate-spin" />
          <span>Checking link…</span>
        </div>
      )}

      {/* Coords extracted — green tick with lat/lng */}
      {status === "coords" && resolvedCoords && (
        <div
          className="mt-1 text-[11px] text-emerald-700 inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-google-maps-url-ok`}
        >
          <Check size={11} />
          <span>
            Location detected: {resolvedCoords.lat.toFixed(4)}, {resolvedCoords.lng.toFixed(4)}
          </span>
        </div>
      )}

      {/* URL saved without coords — short link or place share, still valid */}
      {status === "saved" && (
        <div
          className="mt-1 text-[11px] text-emerald-700 inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-google-maps-url-saved`}
        >
          <Check size={11} />
          <span>Link saved · map pin not previewable from this short URL</span>
        </div>
      )}

      {/* Empty hint */}
      {!raw && emptyHint && (
        <div className="mt-1 text-[10.5px] text-zinc-500">{emptyHint}</div>
      )}
    </>
  );
}

export { GoogleMapsUrlInput };
